import type React from "react";
import type { Ref } from "react";
import { twJoin } from "tailwind-merge";

import { autoCompleteDisabledProperties } from "@/helpers/auto-complete-helpers";
import { twResolve } from "@/helpers/tw-resolve";

import type { DesignSystemElementProps } from "./contracts/dom";
import { fieldControlStateVariants } from "./field-control";

const INPUT_WIDTH_CLASSES = {
  auto: undefined,
  full: "w-full",
  fluid: "min-w-0 flex-1",
  numeric: "w-control-numeric",
  compact: "w-full @sm:w-search-compact",
  standard: "w-full @md:w-search-standard",
  wide: "w-full @md:w-search-wide",
} as const;

const INPUT_APPEARANCE_CLASSES = {
  default: undefined,
  quiet:
    "border-border-secondary bg-background-default py-space-xs text-body placeholder:text-text-tertiary hover:border-border-secondary focus-visible:border-border-secondary focus-visible:ring-transparent",
} as const;

export type InputWidth = keyof typeof INPUT_WIDTH_CLASSES;
export type InputAppearance = keyof typeof INPUT_APPEARANCE_CLASSES;

export interface InputProps extends Omit<DesignSystemElementProps<"input">, "prefix"> {
  ref?: Ref<HTMLInputElement>;
  prefix?: React.ReactNode;
  postfix?: React.ReactNode;
  isError?: boolean;
  readOnlyTextTone?: "primary" | "secondary";
  width?: InputWidth;
  appearance?: InputAppearance;
}

export function Input({
  prefix,
  value,
  postfix,
  isError,
  disabled,
  readOnly,
  readOnlyTextTone = "secondary",
  ref,
  width = "auto",
  appearance = "default",
  ...props
}: InputProps): React.ReactNode {
  return (
    <div className={twJoin("relative", INPUT_WIDTH_CLASSES[width])}>
      {prefix && <InputExtra position="start">{prefix}</InputExtra>}
      <input
        {...props}
        value={value}
        className={twResolve(
          "h-control-md w-full appearance-none rounded-lg border border-border-secondary bg-background-default px-field-inline py-field-block text-body placeholder:text-text-tertiary",
          fieldControlStateVariants({ invalid: isError }),
          (disabled || readOnly) &&
            "border-border-secondary bg-background-secondary text-text-secondary hover:border-border-secondary",
          readOnly && !disabled && readOnlyTextTone === "primary" && "text-text-primary",
          prefix && "pl-field-adornment",
          postfix && "pr-field-adornment",
          INPUT_APPEARANCE_CLASSES[appearance],
        )}
        // Prevent default scrolling behaviour for number input
        onWheel={props.type === "number" ? (event) => event.currentTarget.blur() : undefined}
        aria-invalid={isError}
        {...{ readOnly, ref, disabled }}
        {...(props.autoComplete === undefined || props.autoComplete === "off" ? autoCompleteDisabledProperties : {})}
      />
      {postfix && <InputExtra position="end">{postfix}</InputExtra>}
    </div>
  );
}

interface InputExtraProps {
  children: React.ReactNode;
  position: "start" | "end";
  align?: "center" | "edge";
}

export function InputExtra({ align = "center", children, position }: InputExtraProps): React.ReactNode {
  return (
    <div
      className={twJoin(
        "absolute top-0 flex h-full w-field-adornment items-center text-text-secondary",
        position === "start" && "left-0",
        position === "end" && "right-0",
        align === "center" ? "justify-center" : "justify-end pr-space-sm",
      )}
    >
      {children}
    </div>
  );
}
